import React, { useState, useRef, useEffect } from 'react'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

const EXPIRED_ERRORS = [
	'Verification code expired',
	'Too many attempts',
	'Maximum resend limit reached',
]

function isExpiredError(message) {
	return EXPIRED_ERRORS.some(err => message?.toLowerCase().includes(err.toLowerCase()))
}

function TwoFactorVerification({ pendingLoginToken, maskedEmail, onCancel, onSuccess }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
	const [loading, setLoading] = useState(false)
	const [errorText, setErrorText] = useState('')
	const [expired, setExpired] = useState(false)
	const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
	const inputRefs = useRef([])

	useEffect(() => {
		inputRefs.current[0]?.focus()
	}, [])

	useEffect(() => {
		if (resendCooldown <= 0) return
		const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000)
		return () => clearTimeout(timer)
	}, [resendCooldown])

	const handleDigitChange = (index, value) => {
		if (!/^\d?$/.test(value)) return

		const updated = [...digits]
		updated[index] = value
		setDigits(updated)
		setErrorText('')

		if (value && index < OTP_LENGTH - 1) {
			inputRefs.current[index + 1]?.focus()
		}

		if (value && index === OTP_LENGTH - 1 && updated.every(d => d)) {
			handleSubmit(updated.join(''))
		}
	}

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus()
		}
	}

	const handlePaste = e => {
		e.preventDefault()
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
		if (!pasted) return

		const updated = [...digits]
		for (let i = 0; i < pasted.length; i++) {
			updated[i] = pasted[i]
		}
		setDigits(updated)

		const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
		inputRefs.current[focusIndex]?.focus()

		if (updated.every(d => d)) {
			handleSubmit(updated.join(''))
		}
	}

	const handleSubmit = async code => {
		const otpCode = code || digits.join('')
		if (otpCode.length !== OTP_LENGTH) {
			setErrorText('Please enter the full 6-digit code')
			return
		}

		setLoading(true)
		setErrorText('')

		try {
		const response = await fetch('/api/auth/verify-two-factor', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ pendingLoginToken, otpCode }),
		})

			const data = await response.json()

			if (response.ok && data.token) {
				onSuccess(data)
			} else {
				const errorMsg = data.error || 'Verification failed'
				setErrorText(errorMsg)
				if (isExpiredError(errorMsg)) {
					setExpired(true)
				} else {
					setDigits(Array(OTP_LENGTH).fill(''))
					inputRefs.current[0]?.focus()
				}
			}
		} catch {
			setErrorText('An error occurred. Please try again.')
			setDigits(Array(OTP_LENGTH).fill(''))
			inputRefs.current[0]?.focus()
		} finally {
			setLoading(false)
		}
	}

	const handleResend = async () => {
		if (resendCooldown > 0) return

		try {
		const response = await fetch('/api/auth/resend-two-factor', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			body: JSON.stringify({ pendingLoginToken }),
		})

			const data = await response.json()

			if (response.ok) {
				setResendCooldown(RESEND_COOLDOWN_SECONDS)
				setErrorText('')
				setDigits(Array(OTP_LENGTH).fill(''))
				inputRefs.current[0]?.focus()
			} else {
				const errorMsg = data.error || 'Failed to resend code'
				setErrorText(errorMsg)
				if (isExpiredError(errorMsg)) {
					setExpired(true)
				}
			}
		} catch {
			setErrorText('Failed to resend code')
		}
	}

	if (expired) {
		return (
			<div className={`p-8 rounded-lg w-full max-w-md text-center ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg text-gray-900' : 'bg-gray-800 ring-1 ring-white/10 shadow-xl text-white'}`}>
				<div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isLight ? 'bg-red-100' : 'bg-red-500/20'}`}>
					<svg className={`w-6 h-6 ${isLight ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
					</svg>
				</div>
				<h2 className="text-xl font-semibold mb-2">{errorText}</h2>
				<p className={`text-sm mb-6 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
					Please go back and try again.
				</p>
				<button
					onClick={onCancel}
					className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1d9bf0] hover:bg-[#1a8cd8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
				>
					Go back to login
				</button>
			</div>
		)
	}

	return (
		<div className={`p-8 rounded-lg w-full max-w-md ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg text-gray-900' : 'bg-gray-800 ring-1 ring-white/10 shadow-xl text-white'}`}>
			<h2 className="text-2xl font-semibold mb-2 text-center">Verify your identity</h2>
			<p className={`text-sm mb-6 text-center ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
				We sent a verification code to <span className={`font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>{maskedEmail}</span>
			</p>

			{errorText && (
				<div className={`text-sm mb-4 text-center ${isLight ? 'text-red-600' : 'text-red-400'}`}>{errorText}</div>
			)}

			<div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
				{digits.map((digit, i) => (
					<input
						key={i}
						ref={el => (inputRefs.current[i] = el)}
						type="text"
						inputMode="numeric"
						maxLength={1}
						value={digit}
						onChange={e => handleDigitChange(i, e.target.value)}
						onKeyDown={e => handleKeyDown(i, e)}
						className={`w-12 h-14 text-center text-xl font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isLight ? 'bg-white border border-gray-300 text-gray-900' : 'bg-gray-700 border border-gray-600 text-white'}`}
						disabled={loading}
					/>
				))}
			</div>

			<button
				onClick={() => handleSubmit()}
				disabled={loading || digits.some(d => !d)}
				className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1d9bf0] hover:bg-[#1a8cd8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4 ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
			>
				{loading ? 'Verifying...' : 'Verify'}
			</button>

			<div className="flex justify-between items-center text-sm">
				<button
					onClick={handleResend}
					disabled={resendCooldown > 0}
					className={`font-medium ${
						resendCooldown > 0
							? 'text-gray-500 cursor-not-allowed'
							: 'text-sky-400 hover:text-sky-300 hover:underline cursor-pointer'
					}`}
				>
					{resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
				</button>

				<button
					onClick={onCancel}
					className={`cursor-pointer ${isLight ? 'text-gray-700 hover:text-gray-900' : 'text-gray-300 hover:text-white'}`}
				>
					Cancel
				</button>
			</div>
		</div>
	)
}

export default TwoFactorVerification
